/**
 * E2B (Code Execution) Tool Configurations
 *
 * Tools for executing Python code in sandboxed environment.
 */ import { defineToolConfig, hasProperty } from '../tool-display-config';
// ============================================================================
// Tool Configurations
// ============================================================================
const executePythonConfig = defineToolConfig({
    id: 'execute-python',
    aliases: [
        'executePython',
        'executePythonTool'
    ],
    formatSuccess: (output)=>{
        if (!output.success) {
            return 'Execution failed';
        }
        const time = output.executionTime ? ` in ${(output.executionTime / 1000).toFixed(2)}s` : '';
        if (output.stdout) {
            const lines = output.stdout.trim().split('\n');
            if (lines.length === 1 && lines[0].length < 50) {
                return `Result: ${lines[0]}${time}`;
            }
            return `Executed successfully${time} (${lines.length} lines of output)`;
        }
        if (output.result !== undefined) {
            const resultStr = JSON.stringify(output.result);
            if (resultStr.length < 50) {
                return `Result: ${resultStr}${time}`;
            }
            return `Executed successfully${time}`;
        }
        return `Executed successfully${time}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'stderr') && error.stderr) {
            const stderr = String(error.stderr);
            // Extract the last line which usually has the error message
            const lines = stderr.trim().split('\n');
            const lastLine = lines[lines.length - 1];
            if (lastLine.length < 100) {
                return `Error: ${lastLine}`;
            }
        }
        if (hasProperty(error, 'error')) {
            return `Execution error: ${error.error}`;
        }
        if (hasProperty(error, 'message')) {
            return `Execution error: ${error.message}`;
        }
        return 'Python execution failed';
    },
    formatInput: (input)=>{
        if (!input?.code) return [];
        const lines = input.code.trim().split('\n');
        const preview = lines[0].slice(0, 40) + (lines[0].length > 40 || lines.length > 1 ? '...' : '');
        return [
            {
                label: 'Code',
                value: preview
            },
            {
                label: 'Lines',
                value: String(lines.length)
            }
        ];
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>!output.success
});
const runPythonCodeConfig = defineToolConfig({
    id: 'run-python-code',
    aliases: [
        'runPythonCode',
        'runPythonCodeTool'
    ],
    formatSuccess: (output)=>{
        if (!output.success) {
            return 'Execution failed';
        }
        const time = output.executionTime ? ` in ${(output.executionTime / 1000).toFixed(2)}s` : '';
        if (output.output) {
            const lines = output.output.trim().split('\n');
            if (lines.length === 1 && lines[0].length < 50) {
                return `Output: ${lines[0]}${time}`;
            }
            return `Executed successfully${time} (${lines.length} lines)`;
        }
        return `Executed successfully${time}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'error')) {
            return `REPL error: ${error.error}`;
        }
        if (hasProperty(error, 'message')) {
            return `REPL error: ${error.message}`;
        }
        return 'Python REPL execution failed';
    },
    formatInput: (input)=>{
        if (!input?.code) return [];
        const lines = input.code.trim().split('\n');
        const preview = lines[0].slice(0, 40) + (lines[0].length > 40 || lines.length > 1 ? '...' : '');
        return [
            {
                label: 'Code',
                value: preview
            },
            {
                label: 'Lines',
                value: String(lines.length)
            }
        ];
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>!output.success
});
const installPackagesConfig = defineToolConfig({
    id: 'install-python-packages',
    aliases: [
        'installPythonPackages',
        'installPythonPackagesTool'
    ],
    formatSuccess: (output)=>{
        if (!output.success) return 'Package installation failed';
        const count = output.packages?.length ?? 0;
        if (count === 1) {
            return `Installed ${output.packages[0]}`;
        }
        return `Installed ${count} packages`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'error')) {
            return `Installation failed: ${error.error}`;
        }
        if (hasProperty(error, 'message')) {
            return `Installation failed: ${error.message}`;
        }
        return 'Failed to install Python packages';
    },
    formatInput: (input)=>{
        if (!input?.packages?.length) return [];
        return [
            {
                label: 'Packages',
                value: input.packages.join(', ')
            }
        ];
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output;
    },
    isErrorResult: (output)=>!output.success
});
// ============================================================================
// Export
// ============================================================================
export const e2bToolConfigs = [
    executePythonConfig,
    runPythonCodeConfig,
    installPackagesConfig
];
